import { useNavigate } from "react-router-dom";
import { Grid } from "@/comn/components";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useStore, useToast, useResource, useModal, usePage, useGrid } from "@/comn/hooks";
import {
    URLS,
    APIS,
    SF_CSTM_OFCR_INFO_FORM,
    SG_USR_CSTM_LIST,
} from "@/tra/tancis/ptli/usr/cstmofcr/services/PtliCstmOfcrService";
import { useEffect } from "react";

/*
 * Customs officer management registration
 * !== 세관직원관리 등록 ==!
 */
export const PTLI0902002S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "comnCd", comnCd: "PTL0003" },
            { area: "cstmCd" },
            { area: "orgzSub" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Customs officer management Form !== 세관직원관리 Form ==!
        cstmOfcrForm: useForm({
            defaultSchema: SF_CSTM_OFCR_INFO_FORM,
            defaultValues: {
                actnUsrYn: "N",
                smsRcpnYn: "N",
                emlRcpnYn: "Y",
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== Grid 정의 ==!
     */
    const grid = {
        usrCstmList: useGrid({ defaultSchema: SG_USR_CSTM_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Create Customs officer !== 세관직원 등록 ==!
        createCstmOfcr: useFetch({
            api: (data) => APIS.createCstmOfcr({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        navigate(URLS.ptli0902001q);
                    },
                    close: false,
                });
            },
            onError: (error) => {
                const errorData = error.response.data;

                if (error.response.status === 400) {
                    form.cstmOfcrForm.setErrors(errorData.errors);

                    modal.openModal({
                        content: "msg.00006", // An error occurred during processing.
                    });

                    return;
                }

                const { errorCode, errorMessage } = errorData;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
        // Section search by customs !== 섹션별 세관 조회 ==!
        getCstmList: useFetch({
            api: () => APIS.getOrgzList(form.cstmOfcrForm.getValues()),
            enabled: false,
            onSuccess: (data) => {
                if (data.orgzList.content.length > 0) {
                    form.cstmOfcrForm.setSchema("cstmOfceCd", {
                        options: data.orgzList.content.map((item: any, index: number) => ({
                            label: item.cstmOrgzNm,
                            value: item.cstmOrgzCd,
                        })),
                    });
                }

                handler.getCstmRoleList();
            },
            onError: () => {},
            showToast: true,
        }),

        // unit search by section !== 섹션별 유닛 조회 ==!
        getUnitList: useFetch({
            api: () => APIS.getOrgzList(form.cstmOfcrForm.getValues()),
            enabled: false,
            onSuccess: (data) => {
                if (data.orgzList.content.length > 0) {
                    form.cstmOfcrForm.setSchema("cstmUnitOrgzCd", {
                        options: data.orgzList.content.map((item: any) => ({
                            label: item.cstmOrgzNm,
                            value: item.cstmOrgzCd,
                        })),
                    });
                }

                handler.getCstmRoleList();
            },
            onError: () => {},
            showToast: true,
        }),
        // Section search by customs role !== 조직 역할 목록 조회 ==!
        getCstmRoleList: useFetch({
            api: (objParam) => APIS.getCstmRoleList(objParam),
            enabled: !comnUtils.isEmpty(form.cstmOfcrForm.getValue("cstmSctnOrgzCd")),
            onSuccess: (data) => {
                if (data.cstmRoleList.content.length > 0) {
                    form.cstmOfcrForm.setSchema("roleId", {
                        options: data.cstmRoleList.content.map((item: any) => ({
                            label: item.roleNm,
                            value: item.roleId,
                        })),
                    });
                } else {
                    form.cstmOfcrForm.setSchema("roleId", { options: [] });
                    form.cstmOfcrForm.setValue("roleId", "");
                }
            },
            onError: () => {},
            showToast: true,
        }),

        getTinNoLst: useFetch({
            api: () => {
                return APIS.getTinNoLst(usrTin, "N");
            },
            onSuccess: (data) => {
                const tinNoList = data.tinNoList.content;

                if (tinNoList.length == 1) {
                    const tinNoInfo = tinNoList[0];
                    form.cstmOfcrForm.setValue("usrTin", tinNoInfo.tinNo);
                    form.cstmOfcrForm.setValue("usrNm", tinNoInfo.coNm);
                    form.cstmOfcrForm.setValue("usrTelno", tinNoInfo.rprsTelno);
                    form.cstmOfcrForm.setValue("usrEmlAddr", tinNoInfo.rprsEml);
                } else {
                    toast.showToast({ type: "error", content: "msg.clr.00004" });
                }
            },
            onError: () => {},
        }),

        // Inquire user application information!== 사용자 신청 정보 조회 ==!
        getUsrRgsrAplyInfo: useFetch({
            api: (data) => APIS.getUsrRgsrAplyInfo(data),
            enabled: false,
            onSuccess: (data) => {
                form.cstmOfcrForm.setValue("aprvPrcssStatCdNm", "");

                if (data.usrAplyInfo.content) {
                    form.cstmOfcrForm.setError("cstmOfcrNo", {
                        message: "msg.com.00107", // Already registered user. !== 이미 등록된 사용자입니다. ==!
                        type: "error",
                    });

                    form.cstmOfcrForm.setValue("aprvPrcssStatCdNm", data.usrAplyInfo.content.aprvPrcssStatCdNm);
                    return;
                }

                const content = fetch.getHrUsrInfo.getData().usrHrInfo.content;
                form.cstmOfcrForm.setValue("usrDmnId", content.usrDmnId);
                form.cstmOfcrForm.setValue("usrNm", content.usrNm);
                form.cstmOfcrForm.setValue("usrTelno", content.usrTelno);
                form.cstmOfcrForm.setValue("usrEmlAddr", content.usrEmlAddr);
                form.cstmOfcrForm.setValue("usrTin", content.usrTin);

                form.cstmOfcrForm.clearErrors("cstmOfcrNo");
            },
            onError: () => {},
            showToast: true,
        }),

        // HR system employee number inquiry !== HR시스템 직원번호 조회 ==!
        getHrUsrInfo: useFetch({
            api: (cstmOfcrNo) => APIS.getHrUsrInfo(cstmOfcrNo),
            enabled: false,
            onSuccess: (data) => {
                if (data.usrHrInfo.content) {
                    const { usrDmnId, cstmOfcrNo } = data.usrHrInfo.content;
                    fetch.getUsrRgsrAplyInfo.fetch({ usrDmnId, cstmOfcrNo });
                } else {
                    form.cstmOfcrForm.setError("cstmOfcrNo", {
                        message: "msg.com.00108", // There is no customs employee number registered in the HR system. !== HR시스템에 등록된 세관직원번호가 없습니다.  ==!
                        type: "error",
                    });
                    const actnUsrYn = form.cstmOfcrForm.getValue("actnUsrYn");
                    form.cstmOfcrForm.clearValues();
                    form.cstmOfcrForm.setValue("actnUsrYn", actnUsrYn);
                }
            },
            onError: () => {},
            showToast: true,
        }),

        // Check TIN validity !== TIN 유효성을 체크 후 사용자 정보를 저장한다. ==!
        getUsrTinInfo: useFetch({
            api: (usrTin) => APIS.getUsrTinInfo(usrTin),
            enabled: false,
            onSuccess: (data) => {
                if (!data.usrTinInfo.content) {
                    form.cstmOfcrForm.setError("usrTin", {
                        message: "msg.ptl.00729", // Already registered user. !== 등록된 TIN이 없습니다. ==!
                        type: "error",
                    });
                }
            },
            onError: (error) => console.log(error),
            showToast: true,
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onGridDelBtnClick: () => {
            const list = grid.usrCstmList.getChecked();
            if (comnUtils.isEmpty(list)) {
                modal.openModal({ content: "msg.com.00086" }); // No data has been selected. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.com.00049",
                onConfirm: () => {
                    grid.usrCstmList.deleteRow("checkbox");
                },
            });
        },
        onGridAddBtnClick: () => {
            const usrDmnId = form.cstmOfcrForm.getValue("usrDmnId");
            if (comnUtils.isEmpty(usrDmnId)) {
                // Employee information has not been confirmed. !== 직원 정보가 확인되지 않았습니다. ==!
                modal.openModal({ content: "msg.ptl.00805" });
                return;
            }

            grid.usrCstmList.addRow({ usrDmnId, useYn: "Y" });
            handler.chkDuplCstmOfceCd();
        },
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.cstmOfcrForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            // check of duplicate customs office
            const currCstmOfceCd = form.cstmOfcrForm.getValues("cstmOfceCd");
            const usrCstmList = grid.usrCstmList.getData();

            if (
                usrCstmList.some(
                    (item: any) => comnUtils.isEmptyString(item.cstmCd) || comnUtils.isUndefined(item.cstmCd),
                )
            ) {
                toast.showToast({
                    type: "warning",
                    content: t("msg.com.00035", { 0: t("T_USR_CSTM_LIST") + " : " + t("L_CSTM_OFCE") }),
                });
                return;
            } // Please enter {0}. !== {0} 을(를) 입력하여 주세요. ==!

            if (usrCstmList.some((item: any) => item.cstmCd == currCstmOfceCd)) {
                toast.showToast({
                    type: "warning",
                    content: t("msg.cgm.00231", { 0: t("L_CSTM_OFCE") }),
                });
                return;
            } // {0} is already registered. !== {0}가 이미 등록되었습니다. ==!

            if (
                usrCstmList.some((item: any, index: any, self: any) => {
                    return index !== self.findIndex((v: any) => v.cstmCd === item.cstmCd);
                })
            ) {
                toast.showToast({
                    type: "warning",
                    content: t("msg.cgm.00383", { 0: t("T_USR_CSTM_LIST") }),
                });
                return;
            } // Duplicate data exists. ( {0} ) !== 중복된 데이터가 존재합니다. ( {0} ) ==!

            form.cstmOfcrForm.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            const data = form.cstmOfcrForm.getValues();
                            if (typeof data.fileGrpUuid === "object") {
                                data.fileSaveDto = data.fileGrpUuid;
                                data.fileGrpUuid = null;
                            }
                            // Handle attachment file group UUID ? !== 첨부 파일 그룹 UUID 처리 ?==!
                            if (typeof data.fileAtchGrpUuid === "object") {
                                data.atchFileSaveDto = data.fileAtchGrpUuid;
                                data.fileAtchGrpUuid = null;
                            }

                            fetch.createCstmOfcr.fetch({
                                ...data,
                                usrCstmList: grid.usrCstmList.getData(),
                            });
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Section search by customs !== 섹션별 세관 조회 ==!
        getCstmList: (loadYn: any) => {
            if (loadYn !== "Y") {
                form.cstmOfcrForm.setSchema("cstmOfceCd", { options: [] });
                form.cstmOfcrForm.setSchema("cstmUnitOrgzCd", { options: [] });
                form.cstmOfcrForm.setValue("cstmOfceCd", "");
                form.cstmOfcrForm.setValue("cstmUnitOrgzCd", "");
            }

            form.cstmOfcrForm.setValue("cstmOrgzTpCd", "C");

            fetch.getCstmList.fetch();
        },

        // unit search by section !== 세관별 유닛 조회 ==!
        getUnitList: (loadYn: any) => {
            if (loadYn !== "Y") {
                form.cstmOfcrForm.setSchema("cstmUnitOrgzCd", { options: [] });
                form.cstmOfcrForm.setValue("cstmUnitOrgzCd", "");
            }

            form.cstmOfcrForm.setValue("cstmOrgzTpCd", "U");

            fetch.getUnitList.fetch();
        },

        // Section search by customs role !== 세관 역할 조회 ==!
        getCstmRoleList: () => {
            const cstmSctnOrgzCd = form.cstmOfcrForm.getValue("cstmSctnOrgzCd");

            // Initialize role combo if there is no section code !== 섹션 코드 없는 경우 역할 콤보 초기화 ==!
            if (comnUtils.isEmpty(cstmSctnOrgzCd)) {
                form.cstmOfcrForm.setSchema("roleId", { options: [] });
                form.cstmOfcrForm.setValue("roleId", "");
                return;
            }

            const cstmOfceCd = form.cstmOfcrForm.getValue("cstmOfceCd");
            const cstmUnitOrgzCd = form.cstmOfcrForm.getValue("cstmUnitOrgzCd");
            const cstmOrgzTpCd = comnUtils.isEmpty(cstmOfceCd) ? "S" : !comnUtils.isEmpty(cstmUnitOrgzCd) ? "U" : "C";
            const objParam = { cstmSctnOrgzCd, cstmOfceCd, cstmUnitOrgzCd, cstmOrgzTpCd };
            fetch.getCstmRoleList.fetch(objParam);
        },

        changeCstmOfcrNo: () => {
            if (form.cstmOfcrForm.getValue("usrDmnId")) {
                form.cstmOfcrForm.setValue("usrDmnId", "");
                form.cstmOfcrForm.setValue("usrNm", "");
                form.cstmOfcrForm.setValue("usrTelno", "");
                form.cstmOfcrForm.setValue("usrEmlAddr", "");
            }
        },

        onBtnSrchClick: () => {
            const cstmOfcrNo = form.cstmOfcrForm.getValue("cstmOfcrNo");
            if (cstmOfcrNo === undefined) {
                form.cstmOfcrForm.setError("cstmOfcrNo", {
                    message: "msg.com.00005",
                    type: "error",
                });
                return;
            }

            form.cstmOfcrForm.clearErrors("cstmOfcrNo");
            if (cstmOfcrNo) fetch.getHrUsrInfo.fetch(cstmOfcrNo);
        },

        // Check TIN validity !== TIN 유효성을 체크한다. ==!
        getUsrTinInfo: () => {
            const usrTin = form.cstmOfcrForm.getValue("usrTin");
            if (usrTin) {
                form.cstmOfcrForm.clearErrors("usrTin");
                fetch.getUsrTinInfo.fetch(usrTin);
            }
        },

        changeUsrTin: () => {
            if (form.cstmOfcrForm.getValue("usrTin") === undefined) {
                form.cstmOfcrForm.clearErrors("usrTin");
            }
        },

        // check of duplicate customs office
        chkDuplCstmOfceCd: () => {
            const currCstmOfceCd = form.cstmOfcrForm.getValues("cstmOfceCd");
            const usrCstmList = grid.usrCstmList.getData();

            if (usrCstmList.some((item: any) => item.cstmCd == currCstmOfceCd)) {
                toast.showToast({
                    type: "warning",
                    content: t("msg.cgm.00231", { 0: t("L_CSTM_OFCE") }),
                });
                return;
            } // {0} is already registered. !== {0}가 이미 등록되었습니다. ==!

            if (
                usrCstmList.some((item: any, index: any, self: any) => {
                    return (
                        !comnUtils.isUndefined(item.cstmCd) &&
                        index !==
                            self.findIndex((v: any) => !comnUtils.isUndefined(v.cstmCd) && v.cstmCd === item.cstmCd)
                    );
                })
            ) {
                toast.showToast({
                    type: "warning",
                    content: t("msg.cgm.00383", { 0: t("T_USR_CSTM_LIST") }),
                });
                return;
            } // Duplicate data exists. ( {0} ) !== 중복된 데이터가 존재합니다. ( {0} ) ==!
        },
    };

    const usrTin = form.cstmOfcrForm.watch("usrTin");

    useEffect(() => {
        grid.usrCstmList.setOption("checkbox", true);
        grid.usrCstmList.setOption("index", false);

        grid.usrCstmList.setEdit("cell", "cstmCd", true);
        grid.usrCstmList.setEdit("cell", "useYn", true);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Title title={"T_CSTM_OFCR"} titleSize={2} />
                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrForm.schema.cstmOfcrNo}
                                rightButton={{ icon: "search", onClick: handler.onBtnSrchClick }}
                                onChange={handler.changeCstmOfcrNo}
                                required={true}
                            />
                            <Group.Control
                                {...form.cstmOfcrForm.schema.usrTin}
                                rightButton={{ icon: "search", onClick: handler.getUsrTinInfo }}
                                onChange={handler.changeUsrTin}
                                required={true}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.usrDmnId} readOnly={true} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrNm} readOnly={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.usrTelno} readOnly={true} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrEmlAddr} readOnly={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.actnUsrYn} required={true} />
                            <Group.Control {...form.cstmOfcrForm.schema.usrStatCd} required={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.fileGrpUuid} copy={false} />
                            <Group.Control {...form.cstmOfcrForm.schema.fileAtchGrpUuid} copy={false} />
                        </Group.Row>
                    </Group.Section>
                    <Group.Section>
                        <Group.Title title={"T_AFFIL"} titleSize={2} />
                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrForm.schema.cstmSctnOrgzCd}
                                popupParams={{ cstmOrgzTpCd: "S" }}
                                onChange={() => handler.getCstmList("N")}
                                required={true}
                            ></Group.Control>
                            <Group.Control {...form.cstmOfcrForm.schema.roleId} required={true} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrForm.schema.cstmOfceCd}
                                onChange={() => handler.getUnitList("N")}
                                required={true}
                            ></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.cstmOfcrForm.schema.cstmUnitOrgzCd}
                                onChange={() => handler.getCstmRoleList()}
                            ></Group.Control>
                        </Group.Row>
                    </Group.Section>

                    <Group.Section>
                        <Group.Title title={"T_SMS_EML_RCPN_YN"} titleSize={2} />
                        <Group.Row>
                            <Group.Control {...form.cstmOfcrForm.schema.smsRcpnYn} required={true} />
                            <Group.Control {...form.cstmOfcrForm.schema.emlRcpnYn} required={true} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section gap={2}>
                        <Layout>
                            <Layout.Left>
                                <Group.Title title={"T_USR_CSTM_LIST"} titleSize={2} />
                            </Layout.Left>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                        <Grid {...grid.usrCstmList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Button role="list" onClick={() => navigate(URLS.ptli0902001q)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.onSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
